import { useCallback, useMemo, useReducer } from "react"
import dayjs, { Dayjs } from "utils/dayjs"

import { IApiLink } from "@/types"
import { AssetOwnershipType } from "models/Asset"
import { LmidModalMode, CopyAssetSourceKind } from "./copyAssetMatrix"
import {
    CopyAssetAction,
    CopyAssetSharedKey,
    ICopyAssetLmidOverwrite,
    ICopyAssetSharedValues,
    ICopyAssetSourceValues,
    canAddRow,
    canRemoveRow,
    copyAssetRowsReducer,
    createInitialState,
    editedRowCount,
    firstInvalidRowId,
    readyRowCount,
} from "./copyAssetRows"

/**
 * State of the copy dialog: the shared values, one entry per copy, and the
 * handlers that keep the two interdependent dropdowns consistent.
 *
 * The reducer holds all rules and is unit-tested on its own; this hook only
 * binds it to React and exposes the derived values the dialog renders.
 */
export default function useCopyAssetRows({
    sourceKind,
    ownershipType,
    sourceOwnershipType,
    isSecondhand,
    source,
    today = dayjs().startOf("day"),
}: {
    sourceKind: CopyAssetSourceKind
    ownershipType: AssetOwnershipType
    sourceOwnershipType: AssetOwnershipType
    isSecondhand: boolean
    source: ICopyAssetSourceValues
    today?: Dayjs
}) {
    const [state, dispatch] = useReducer(
        copyAssetRowsReducer,
        undefined,
        () => createInitialState({ sourceKind, ownershipType, sourceOwnershipType, isSecondhand, source, today }),
    )

    const selectRow = useCallback((id: string) => dispatch({ type: "selectRow", id }), [])
    const addRow = useCallback(() => dispatch({ type: "addRow", source }), [source])
    const duplicateRow = useCallback((id: string) => dispatch({ type: "duplicateRow", id }), [])
    const removeRow = useCallback((id: string) => dispatch({ type: "removeRow", id }), [])

    const setRowValue = useCallback((
        id: string,
        key: "serialNumber" | "serialNumberMachine" | "inventoryNo",
        value: string,
    ) => dispatch({ type: "setRowValue", id, key, value }), [])

    const setRowOverride = useCallback(<K extends CopyAssetSharedKey>(
        id: string,
        key: K,
        value: ICopyAssetSharedValues[K],
    ) => dispatch({ type: "setRowOverride", id, key, value } as CopyAssetAction), [])

    const clearRowOverride = useCallback((id: string, key: CopyAssetSharedKey) =>
        dispatch({ type: "clearRowOverride", id, key }), [])

    const setShared = useCallback(<K extends CopyAssetSharedKey>(key: K, value: ICopyAssetSharedValues[K]) =>
        dispatch({ type: "setShared", key, value } as CopyAssetAction), [])

    const setLmidMode = useCallback((id: string, lmidMode: LmidModalMode) =>
        dispatch({ type: "setLmidMode", id, lmidMode }), [])

    const assignLmid = useCallback((
        id: string,
        lmid: string,
        createAssetLink: IApiLink | null,
        overwrite: ICopyAssetLmidOverwrite | null,
    ) => dispatch({ type: "assignLmid", id, lmid, createAssetLink, overwrite }), [])

    const derived = useMemo(() => ({
        readyCount: readyRowCount(state),
        editedCount: editedRowCount(state),
        firstInvalidId: firstInvalidRowId(state),
        canAdd: canAddRow(state),
        canRemove: canRemoveRow(state),
    }), [state])

    return {
        state,
        ...derived,
        selectRow,
        addRow,
        duplicateRow,
        removeRow,
        setRowValue,
        setRowOverride,
        clearRowOverride,
        setShared,
        setLmidMode,
        assignLmid,
    }
}
